import { FileInputComponent } from '../../file-input/file-input.component';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormGroup, FormControl, AbstractControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  faArrowLeft,
  faArrowRight,
  faCheck,
  faCirclePlus,
  faEdit,
  faImage,
  faPlane,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { getDownloadURL } from 'firebase/storage';
import { EnumAcl } from '@simplyfly/shared';
import { EnumAircraftStatus } from '@simplyfly/shared';
import { getAircraftStatusLabel } from 'src/app/enums/aircraft-status.helpers';
import { getContinentLabel, getCountryLabel } from 'src/app/enums/continent-code.helpers';
import { EnumEnquiryType } from '@simplyfly/shared';
import { IAircraftCompiled } from '@simplyfly/shared';
import { getAircraftCompiledModelTitle } from 'src/app/interfaces/aircraft-compiled.helpers';
import { IAircraftModel } from '@simplyfly/shared';
import { IAircraft } from '@simplyfly/shared';
import { IAirline } from '@simplyfly/shared';
import { IEnquiryFlight } from '@simplyfly/shared';
import { IEnquiry } from '@simplyfly/shared';
import { AclService } from 'src/app/services/acl.service';
import { AircraftCompiledService } from 'src/app/services/aircraft-compiled/aircraft-compiled.service';
import { AircraftService } from 'src/app/services/aircrafts/aircrafts.service';
import { AircraftModelService } from 'src/app/services/aircraft-models/aircraft-models.service';
import { AirlineService } from 'src/app/services/airlines/airlines.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { generateRandomId } from 'src/app/misc.utils';
import { Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AirlineAutocompleteComponent } from '../../airline-autocomplete/airline-autocomplete.component';

export type AircraftBriefImageField = 'imageOutside' | 'imageInside';
export type AircraftBriefImageSource = 'aircraft' | 'model';

export interface IAircraftBriefImageCandidate {
  url: string;
  source: AircraftBriefImageSource;
  labelKeys: string[];
}

function addBriefImageCandidate(
  candidates: IAircraftBriefImageCandidate[],
  seenUrls: Set<string>,
  url: string | null | undefined,
  source: AircraftBriefImageSource,
  labelKeys: string[] = []
): void {
  if (!url) {
    return;
  }

  if (seenUrls.has(url)) {
    const existingCandidate = candidates.find(candidate => candidate.url === url);
    for (const labelKey of labelKeys) {
      if (existingCandidate && !existingCandidate.labelKeys.includes(labelKey)) {
        existingCandidate.labelKeys.push(labelKey);
      }
    }
    return;
  }

  candidates.push({ url, source, labelKeys });
  seenUrls.add(url);
}

export function buildAircraftBriefImageCandidates(
  aircraft: IAircraft | null,
  model: IAircraftModel | null
): IAircraftBriefImageCandidate[] {
  const candidates: IAircraftBriefImageCandidate[] = [];
  const seenUrls = new Set<string>();

  if (aircraft) {
    addBriefImageCandidate(candidates, seenUrls, aircraft.imageOutsideUrl, 'aircraft', [
      'FLIGHT_BRIEF.DEFAULT_OUTSIDE_IMAGE'
    ]);
    addBriefImageCandidate(candidates, seenUrls, aircraft.imageInsideUrl, 'aircraft', [
      'FLIGHT_BRIEF.DEFAULT_INSIDE_IMAGE'
    ]);
    addBriefImageCandidate(candidates, seenUrls, aircraft.imageFloorPlanUrl, 'aircraft', [
      'FLIGHT_BRIEF.DEFAULT_FLOOR_PLAN_IMAGE'
    ]);

    for (const imageUrl of aircraft.imageUrls ?? []) {
      addBriefImageCandidate(candidates, seenUrls, imageUrl, 'aircraft');
    }
  }

  if (model) {
    addBriefImageCandidate(
      candidates,
      seenUrls,
      model.imageOutsideUrl ?? model.imageUrl,
      'model',
      ['FLIGHT_BRIEF.DEFAULT_OUTSIDE_IMAGE']
    );
    addBriefImageCandidate(candidates, seenUrls, model.imageUrl, 'model', [
      'FLIGHT_BRIEF.DEFAULT_OUTSIDE_IMAGE'
    ]);
    addBriefImageCandidate(candidates, seenUrls, model.imageInsideUrl, 'model', [
      'FLIGHT_BRIEF.DEFAULT_INSIDE_IMAGE'
    ]);
    addBriefImageCandidate(candidates, seenUrls, model.imagePlanUrl, 'model', [
      'FLIGHT_BRIEF.DEFAULT_FLOOR_PLAN_IMAGE'
    ]);
  }

  return candidates;
}

export function getAircraftBriefImageSourceKey(
  field: AircraftBriefImageField,
  imageUrl: string | null | undefined,
  aircraft: IAircraft | null,
  model: IAircraftModel | null
): string | null {
  if (!imageUrl) {
    return null;
  }

  const aircraftDefaultUrl =
    field === 'imageOutside' ? aircraft?.imageOutsideUrl : aircraft?.imageInsideUrl;
  if (aircraftDefaultUrl === imageUrl) {
    return 'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.AIRCRAFT_DEFAULT_IMAGE';
  }

  const aircraftUrls = new Set([
    aircraft?.imageOutsideUrl,
    aircraft?.imageInsideUrl,
    aircraft?.imageFloorPlanUrl,
    ...(aircraft?.imageUrls ?? [])
  ]);
  if (aircraftUrls.has(imageUrl)) {
    return 'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.AIRCRAFT_IMAGE';
  }

  const modelDefaultUrl =
    field === 'imageOutside'
      ? model?.imageOutsideUrl ?? model?.imageUrl
      : model?.imageInsideUrl;
  if (modelDefaultUrl === imageUrl) {
    return 'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.MODEL_DEFAULT_IMAGE';
  }

  const modelUrls = new Set([
    model?.imageOutsideUrl,
    model?.imageUrl,
    model?.imageInsideUrl,
    model?.imagePlanUrl
  ]);
  if (modelUrls.has(imageUrl)) {
    return 'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.MODEL_IMAGE';
  }

  return 'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.CUSTOM_IMAGE';
}

@Component({
    selector: 'app-enquiry-flight-edit-aircraft',
    templateUrl: './enquiry-flight-edit-aircraft.component.html',
    styleUrls: ['./enquiry-flight-edit-aircraft.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FileInputComponent, FormsModule, ReactiveFormsModule, FaIconComponent, AirlineAutocompleteComponent, TranslateModule]
})
export class EnquiryFlightEditAircraftComponent implements OnInit, OnChanges, OnDestroy {
  @Input() enquiry: IEnquiry | null = null;
  @Input() enquiryFlight: IEnquiryFlight | null = null;
  @Output() goPrev: EventEmitter<void> = new EventEmitter();
  @Output() saveAndGoNext: EventEmitter<IEnquiryFlight> = new EventEmitter();

  @ViewChild('modalAircraftAdd', { static: false }) modalAircraftAddElement: ElementRef;
  @ViewChild('modalBriefImage', { static: false }) modalBriefImageElement: ElementRef;
  @ViewChild('briefImageFileInput') briefImageFileInput: FileInputComponent;

  EnumEnquiryType = EnumEnquiryType;

  form: FormGroup = this.resetForm();

  formAircraft: FormGroup = new FormGroup({
    serialNumber: new FormControl(null),
    status: new FormControl(EnumAircraftStatus.active, Validators.required),
    registration: new FormControl(null, Validators.required)
  });

  EnumAcl = EnumAcl;

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faPlane = faPlane;
  faCirclePlus = faCirclePlus;
  faEdit = faEdit;
  faCheck = faCheck;
  faImage = faImage;
  faTrashCan = faTrashCan;

  loadingAircraftsCompiled = false;
  aircraftsCompiled: IAircraftCompiled[] = [];
  aircraftModels: IAircraftModel[] = [];
  loadingAircrafts = false;
  aircrafts: IAircraft[] = [];
  aircraftModelDetails: { [id: string]: IAircraftModel | null } = {};
  private loadedAircraftModelIds = new Set<string>();
  private pendingDefaultImageFields = new Set<'imageOutside' | 'imageInside'>();
  selectedBriefImageField: AircraftBriefImageField | null = null;

  airlinesObj: { [id: string]: IAirline | null } = {};

  getAircraftStatusLabel = getAircraftStatusLabel;

  private subscriptions = new Subscription();

  constructor(
    private aircraftCompiledService: AircraftCompiledService,
    private aircraftService: AircraftService,
    private aircraftModelService: AircraftModelService,
    private airlineService: AirlineService,
    private aclService: AclService,
    private loaderService: LoaderService,
    private appDataService: AppDataService,
    private translateService: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.applyEnquiryFlight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.enquiryFlight &&
      (!changes.enquiryFlight.previousValue ||
        changes.enquiryFlight.previousValue !== changes.enquiryFlight.currentValue)
    ) {
      this.applyEnquiryFlight();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.hideAircraftModal();
    this.hideBriefImageModal();
  }

  hideAircraftModal(): void {
    if (this.modalAircraftAddElement) {
      window.$(this.modalAircraftAddElement.nativeElement).modal('hide');
    }
  }

  openAircraftAddModal(): void {
    window.$(this.modalAircraftAddElement.nativeElement).modal('show');
  }

  get countryCode(): FormControl {
    return this.form.get('countryCode') as FormControl;
  }
  get airlineId(): FormControl {
    return this.form.get('airlineId') as FormControl;
  }
  get airlineTitle(): FormControl {
    return this.form.get('airlineTitle') as FormControl;
  }
  get aircraftModelId(): FormControl {
    return this.form.get('aircraftModelId') as FormControl;
  }
  get aircraftModelTitle(): FormControl {
    return this.form.get('aircraftModelTitle') as FormControl;
  }
  get aircraftId(): FormControl {
    return this.form.get('aircraftId') as FormControl;
  }
  get aircraftRegistration(): FormControl {
    return this.form.get('aircraftRegistration') as FormControl;
  }
  get displayAirlineOnFlightBrief(): FormControl {
    return this.form.get('displayAirlineOnFlightBrief') as FormControl;
  }
  get aircraftHasToilet(): FormControl {
    return this.form.get('aircraftHasToilet') as FormControl;
  }
  get smokingIsAllowed(): FormControl {
    return this.form.get('smokingIsAllowed') as FormControl;
  }
  get capacityMax(): FormControl {
    return this.form.get('capacityMax') as FormControl;
  }
  get imageOutsideUrl(): FormControl {
    return this.form.get('imageOutsideUrl') as FormControl;
  }
  get imageInsideUrl(): FormControl {
    return this.form.get('imageInsideUrl') as FormControl;
  }

  get selectedAircraftModel(): IAircraftModel | null {
    const modelId = this.aircraftModelId?.value;
    if (!modelId) {
      return null;
    }

    return typeof this.aircraftModelDetails[modelId] !== 'undefined'
      ? this.aircraftModelDetails[modelId]
      : null;
  }

  get selectedAircraft(): IAircraft | null {
    const aircraftId = this.aircraftId?.value;
    if (!aircraftId) {
      return null;
    }

    return this.aircrafts.find(aircraft => aircraft.id === aircraftId) ?? null;
  }

  get briefImageCandidates(): IAircraftBriefImageCandidate[] {
    return buildAircraftBriefImageCandidates(this.selectedAircraft, this.selectedAircraftModel);
  }

  get aircraftBriefImageCandidates(): IAircraftBriefImageCandidate[] {
    return this.briefImageCandidates.filter(candidate => candidate.source === 'aircraft');
  }

  get modelBriefImageCandidates(): IAircraftBriefImageCandidate[] {
    return this.briefImageCandidates.filter(candidate => candidate.source === 'model');
  }

  getBriefImageSourceKey(field: AircraftBriefImageField): string | null {
    return getAircraftBriefImageSourceKey(
      field,
      this.form.get(field + 'Url')?.value,
      this.selectedAircraft,
      this.selectedAircraftModel
    );
  }

  navigateToAircraftModelEdit(modelId: string | null | undefined): void {
    if (!modelId) {
      return;
    }

    this.closeModalsAndNavigate(['/admin/aircraft-models', modelId, 'edit']);
  }

  navigateToAircraftEdit(aircraftId: string | null | undefined): void {
    if (!aircraftId) {
      return;
    }

    this.closeModalsAndNavigate(['/admin/aircrafts', aircraftId, 'edit']);
  }

  private closeModalsAndNavigate(commands: any[]): void {
    this.closeAllOpenModals();

    setTimeout(() => {
      this.closeAllOpenModals();
      this.router.navigate(commands);
    }, 0);
  }

  private closeAllOpenModals(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (typeof window.$ === 'function') {
      try {
        window.$('.modal.show').modal('hide');
      } catch (err) {
        // ignore
      }
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');

    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach(backdrop => backdrop.remove());
  }

  getAircraftStatuses(): EnumAircraftStatus[] {
    return Object.values(EnumAircraftStatus);
  }

  applyEnquiryFlight(): void {
    this.form = this.resetForm();
    this.pendingDefaultImageFields.clear();

    if (this.enquiryFlight) {
      for (const imageField of ['imageOutside', 'imageInside'] as const) {
        if (typeof this.enquiryFlight[imageField + 'Url'] === 'undefined') {
          this.pendingDefaultImageFields.add(imageField);
        }
      }

      for (const field in this.form.value) {
        if (typeof this.enquiryFlight[field] !== 'undefined') {
          switch (field) {
            default:
              this.form.get(field).setValue(this.enquiryFlight[field]);
              break;
          }
        }
      }

      if (this.airlineId.value) {
        this.loadAirline(this.airlineId.value);

        this.loadAircraftCompiled();
      }

      if (this.aircraftModelId.value) {
        this.updatedAircraftModelId(false);
      }

      this.form.enable();
    }
  }

  resetForm(): FormGroup {
    return new FormGroup({
      countryCode: new FormControl(null),
      airlineId: new FormControl(null, Validators.required),
      airlineTitle: new FormControl(null),
      aircraftModelId: new FormControl(null, Validators.required),
      aircraftModelTitle: new FormControl(null),
      aircraftId: new FormControl(null),
      aircraftRegistration: new FormControl(null),
      displayAirlineOnFlightBrief: new FormControl(true),
      aircraftHasToilet: new FormControl(true),
      smokingIsAllowed: new FormControl(false),
      capacityMax: new FormControl(null),
      imageOutsideUrl: new FormControl(undefined),
      imageOutsideZoomLevel: new FormControl(1),
      imageInsideUrl: new FormControl(undefined),
      imageInsideZoomLevel: new FormControl(1)
    });
  }

  prev(): void {
    this.goPrev.emit();
  }

  async saveAndNext(): Promise<void> {
    if (this.form.valid) {
      await this.ensureDefaultBriefImagesAreResolved();
      this.saveAndGoNext.emit(this.form.value);
    }
  }

  applyAirline(airline: IAirline | null): void {
    if (airline) {
      this.countryCode.setValue(airline.countryCode);
      this.airlineId.setValue(airline.id);
      this.airlineTitle.setValue(airline.title);

      this.loadAircraftCompiled();
    } else {
      for (const field of [
        'countryCode',
        'airlineId',
        'airlineTitle',
        'aircraftModelId',
        'aircraftModelTitle',
        'aircraftId',
        'aircraftRegistration'
      ]) {
        this[field].setValue(null);
        this[field].updateValueAndValidity();
      }
    }
  }

  loadAircraftCompiled(): void {
    if (this.airlineId.value) {
      this.loadingAircraftsCompiled = true;

      this.subscriptions.add(
        this.aircraftCompiledService
          .getAllForAirline(this.airlineId.value)
          .subscribe((aircraftsCompiled: IAircraftCompiled[]) => {
            this.loadingAircraftsCompiled = true;

            this.aircraftsCompiled = aircraftsCompiled;

            this.updateAircraftModelsFromAircraftsCompiled();

            this.loadingAircraftsCompiled = false;
          })
      );
    }
  }

  updateAircraftModelsFromAircraftsCompiled(): void {
    const aircraftModelsObj: { [key: string]: IAircraftModel } = {};

    for (const aircraftCompiled of this.aircraftsCompiled) {
      const aircraftModelKey = aircraftCompiled.aircraftModelId || aircraftCompiled.type;

      aircraftModelsObj[aircraftModelKey] = {
        id: aircraftCompiled.aircraftModelId,
        family: aircraftCompiled.family,
        title: getAircraftCompiledModelTitle(aircraftCompiled),
        weight: aircraftCompiled.weight,
        volume: aircraftCompiled.volume,
        target: aircraftCompiled.target
      } as IAircraftModel;

      this.loadAircraftModelDetails(aircraftCompiled.aircraftModelId);
    }

    this.aircraftModels = Object.values(aircraftModelsObj);
    this.aircraftModels.sort((a, b) => (a.title > b.title ? 1 : -1));

    // this.setEstimatedArrivalTimeIfNotProvided();

    this.updateAircraftsFromAircraftsCompiled();
  }

  updatedAircraftModelId(resetImages: boolean = true): void {
    if (resetImages) {
      this.resetBriefImagesToDefaults();
    }

    if (this.aircraftModelId.value) {
      for (const aircraftModel of this.aircraftModels) {
        if (aircraftModel.id === this.aircraftModelId.value) {
          this.aircraftModelTitle.setValue(aircraftModel.title);

          break;
        }
      }

      this.loadAircraftModelDetails(this.aircraftModelId.value);
    } else {
      this.aircraftModelTitle.setValue(null);
    }

    this.aircraftModelTitle.updateValueAndValidity();

    this.updateAircraftsFromAircraftsCompiled();
    this.applyDefaultBriefImages();
  }

  updateAircraftsFromAircraftsCompiled(): void {
    this.aircrafts = [];

    if (this.airlineId.value && this.aircraftModelId.value) {
      this.loadingAircrafts = true;
      this.subscriptions.add(
        this.aircraftService
          .getAllForAirlineAndModel(this.airlineId.value, this.aircraftModelId.value)
          .subscribe((aircrafts: IAircraft[]) => {
            this.loadingAircrafts = true;

            this.aircrafts = aircrafts;

            this.aircrafts.sort((a, b) => (a.registration > b.registration ? 1 : -1));

            let aircraftSelectedIsInList = false;
            for (const aircraft of this.aircrafts) {
              if (aircraft.id === this.aircraftId.value) {
                aircraftSelectedIsInList = true;
                break;
              }
            }

            if (!aircraftSelectedIsInList) {
              this.aircraftId.setValue(null);
              this.aircraftId.updateValueAndValidity();
            }

            this.updatedAircraftId(false);

            this.loadingAircrafts = false;
          })
      );

      this.aircrafts.sort((a, b) => (a.registration > b.registration ? 1 : -1));
    }
  }

  updatedAircraftId(resetImages: boolean = true): void {
    if (resetImages) {
      this.resetBriefImagesToDefaults();
    }

    if (this.aircraftId.value) {
      for (const aircraft of this.aircrafts) {
        if (aircraft.id === this.aircraftId.value) {
          this.applyAircraftInfoToForm(aircraft);
          break;
        }
      }
    } else {
      this.applyAircraftInfoToForm(null);
    }

    this.applyDefaultBriefImages();
  }

  private loadAircraftModelDetails(modelId: string): void {
    if (!modelId || typeof this.aircraftModelDetails[modelId] !== 'undefined') {
      return;
    }

    this.aircraftModelDetails[modelId] = null;

    this.subscriptions.add(
      this.aircraftModelService.getFromId(modelId).subscribe((model: IAircraftModel | null) => {
        this.aircraftModelDetails[modelId] = model;
        this.loadedAircraftModelIds.add(modelId);
        this.applyDefaultBriefImages();
      })
    );
  }

  applyAircraftInfoToForm(aircraft: IAircraft | null): void {
    if (aircraft) {
      this.aircraftRegistration.setValue(
        aircraft.registration !== ''
          ? aircraft.registration
          : this.translateService.instant('GENERAL.UNKNOWN')
      );
    } else {
      this.aircraftRegistration.setValue(null);
    }

    this.aircraftRegistration.updateValueAndValidity();
  }

  selectBriefImage(field: 'imageOutside' | 'imageInside', imageUrl: string): void {
    this.pendingDefaultImageFields.delete(field);
    this.form.get(field + 'Url').setValue(imageUrl);
    this.form.get(field + 'ZoomLevel').setValue(1);
  }

  openBriefImageModal(field: AircraftBriefImageField): void {
    this.selectedBriefImageField = field;
    window.$(this.modalBriefImageElement.nativeElement).modal('show');
  }

  hideBriefImageModal(): void {
    if (this.modalBriefImageElement) {
      window.$(this.modalBriefImageElement.nativeElement).modal('hide');
    }
  }

  useBriefImage(imageUrl: string): void {
    if (!this.selectedBriefImageField) {
      return;
    }

    this.selectBriefImage(this.selectedBriefImageField, imageUrl);
    this.hideBriefImageModal();
  }

  deleteBriefImage(field: 'imageOutside' | 'imageInside'): void {
    this.pendingDefaultImageFields.delete(field);
    this.form.get(field + 'Url').setValue(null);
    this.form.get(field + 'ZoomLevel').setValue(1);
  }

  changeBriefImageFile(file: File, field: 'imageOutside' | 'imageInside'): void {
    if (!file) {
      return;
    }

    this.loaderService.presentLoader();
    this.form.disable();

    const result = this.appDataService.upload(
      'flight-briefs/images',
      generateRandomId(),
      file
    );

    this.subscriptions.add(
      this.appDataService.fromTask(result.task).subscribe({
        complete: async () => {
          try {
            const downloadUrl = await getDownloadURL(result.ref);
            this.selectBriefImage(field, downloadUrl);

            this.form.enable();
            this.briefImageFileInput?.reset();
            this.loaderService.hideLoaderOnSuccess();
            this.hideBriefImageModal();
          } catch (err) {
            this.form.enable();
            if (this.briefImageFileInput) { this.briefImageFileInput.showError('GENERAL.FILE_INPUT.UPLOAD_FAILED'); }
            this.loaderService.hideLoaderOnFailure(err);
          }
        },
        error: err => {
          this.form.enable();
          if (this.briefImageFileInput) { this.briefImageFileInput.showError('GENERAL.FILE_INPUT.UPLOAD_FAILED'); }
          this.loaderService.hideLoaderOnFailure(err);
        }
      })
    );
  }

  private resetBriefImagesToDefaults(): void {
    for (const imageField of ['imageOutside', 'imageInside'] as const) {
      this.pendingDefaultImageFields.add(imageField);
      this.form.get(imageField + 'Url').setValue(null);
      this.form.get(imageField + 'ZoomLevel').setValue(1);
    }
  }

  private applyDefaultBriefImages(): void {
    if (!this.pendingDefaultImageFields.size) {
      return;
    }

    const aircraft = this.selectedAircraft;
    const modelId = this.aircraftModelId.value;
    const model = this.selectedAircraftModel;

    if (this.aircraftId.value && !aircraft) {
      return;
    }

    if (modelId && !this.loadedAircraftModelIds.has(modelId)) {
      return;
    }

    const defaultUrls: Record<'imageOutside' | 'imageInside', string | null> = {
      imageOutside:
        aircraft?.imageOutsideUrl || model?.imageOutsideUrl || model?.imageUrl || null,
      imageInside: aircraft?.imageInsideUrl || model?.imageInsideUrl || null
    };
    const defaultZoomLevels: Record<'imageOutside' | 'imageInside', number> = {
      imageOutside:
        aircraft?.imageOutsideUrl && typeof (aircraft as any).imageOutsideZoomLevel === 'number'
          ? (aircraft as any).imageOutsideZoomLevel
          : 1,
      imageInside:
        aircraft?.imageInsideUrl && typeof (aircraft as any).imageInsideZoomLevel === 'number'
          ? (aircraft as any).imageInsideZoomLevel
          : 1
    };

    for (const imageField of [...this.pendingDefaultImageFields]) {
      this.form.get(imageField + 'Url').setValue(defaultUrls[imageField]);
      this.form.get(imageField + 'ZoomLevel').setValue(defaultZoomLevels[imageField]);
      this.pendingDefaultImageFields.delete(imageField);
    }
  }

  private async ensureDefaultBriefImagesAreResolved(): Promise<void> {
    if (!this.pendingDefaultImageFields.size) {
      return;
    }

    const aircraft =
      this.selectedAircraft ??
      (this.aircraftId.value
        ? await firstValueFrom(this.aircraftService.getFromId(this.aircraftId.value))
        : null);
    const model =
      this.selectedAircraftModel ??
      (this.aircraftModelId.value
        ? await firstValueFrom(this.aircraftModelService.getFromId(this.aircraftModelId.value))
        : null);
    const defaultUrls: Record<'imageOutside' | 'imageInside', string | null> = {
      imageOutside:
        aircraft?.imageOutsideUrl || model?.imageOutsideUrl || model?.imageUrl || null,
      imageInside: aircraft?.imageInsideUrl || model?.imageInsideUrl || null
    };
    const defaultZoomLevels: Record<'imageOutside' | 'imageInside', number> = {
      imageOutside:
        aircraft?.imageOutsideUrl && typeof (aircraft as any).imageOutsideZoomLevel === 'number'
          ? (aircraft as any).imageOutsideZoomLevel
          : 1,
      imageInside:
        aircraft?.imageInsideUrl && typeof (aircraft as any).imageInsideZoomLevel === 'number'
          ? (aircraft as any).imageInsideZoomLevel
          : 1
    };

    for (const imageField of [...this.pendingDefaultImageFields]) {
      this.form.get(imageField + 'Url').setValue(defaultUrls[imageField]);
      this.form.get(imageField + 'ZoomLevel').setValue(defaultZoomLevels[imageField]);
      this.pendingDefaultImageFields.delete(imageField);
    }
  }

  loadAirline(airlineId: string): void {
    if (typeof this.airlinesObj[airlineId] === 'undefined') {
      this.airlinesObj[airlineId] = null;

      this.subscriptions.add(
        this.airlineService.getFromId(airlineId).subscribe((airline: IAirline | null) => {
          this.airlinesObj[airlineId] = airline;
        })
      );
    }
  }

  async setValueToFormControl($event: {
    fields: {
      name: string;
      value: any;
    }[];
  }): Promise<void> {
    for (const field of $event.fields) {
      const nameList = field.name.split('.');

      let formControl: AbstractControl = this.form;
      for (const name of nameList) {
        formControl = formControl.get(name);
      }

      if (field.name === 'airlineId') {
        this.applyAirline(field.value as IAirline | null);
      } else {
        formControl.setValue(field.value);
      }

      formControl.markAsTouched();
      formControl.updateValueAndValidity();
    }
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  async saveAircraft(): Promise<void> {
    this.formAircraft.markAsTouched();

    if (this.formAircraft.status === 'VALID') {
      const data: IAircraft = Object.assign({}, this.formAircraft.value);

      for (const field in data) {
        if (typeof data[field] === 'undefined') {
          data[field] = null;
        }
      }

      this.loaderService.presentLoader();

      this.formAircraft.disable();

      data.aircraftModelId = this.form.value.aircraftModelId ?? null;

      for (const aircraftModel of this.aircraftModels) {
        if (aircraftModel.id === data.aircraftModelId) {
          data.model = aircraftModel.family ?? null;
          data.type = aircraftModel.slug ?? data.model;

          break;
        }
      }

      data.airlineContinentCode = this.airlinesObj[this.airlineId.value]?.continentCode ?? null;
      data.airlineCountryCode = this.airlinesObj[this.airlineId.value]?.countryCode ?? null;
      data.airlineContinentTitle = this.airlinesObj[this.airlineId.value]?.continentCode
        ? getContinentLabel(this.airlinesObj[this.airlineId.value]?.continentCode)
        : null;
      data.airlineCountryTitle = this.airlinesObj[this.airlineId.value]?.countryCode
        ? getCountryLabel(this.airlinesObj[this.airlineId.value]?.countryCode)
        : null;
      data.airlineId = this.form.value.airlineId;
      data.airlineTitle = this.airlinesObj[this.airlineId.value]?.title ?? null;

      try {
        data.id = await this.aircraftService.create(data);

        if (data.id) {
          this.form.get('aircraftId').setValue(data.id);

          this.applyAircraftInfoToForm(data);
        }

        this.formAircraft.reset();

        this.hideAircraftModal();

        await this.loaderService.hideLoaderOnSuccess();
      } catch (err) {
        await this.loaderService.hideLoaderOnFailure(err);

        this.formAircraft.enable();
      }
    }
  }
}
